<template>
  <v-container fluid>
    <div class="d-flex justify-space-between align-center">
      <h2>{{ $tc('vehicle', 2) }}</h2>
      <div>
        <v-btn
          v-if="userLoaded && user.can('create_vehicle')"
          outlined
          color="primary"
          to="/vehicle/create">
          <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
          {{ $t('new_vehicle' )}}
        </v-btn>
        <v-btn
          class="ml-2"
          outlined
          color="primary"
          to="/vehicle/map">
          <v-icon class="mr-1 ml-n2">mdi-map-search</v-icon>
          {{ $t('vehicle_map' )}}
        </v-btn>
      </div>
    </div>
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col xl="4" lg="4" md="6" cols="12">
          <v-text-field
            :label="$t('name')"
            v-model="searchCriteria.name"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-truck">
          </v-text-field>
        </v-col>
        <v-col xl="4" lg="4" md="6" cols="12">
          <v-select
            :label="$t('exclude_deleted')"
            v-model="searchCriteria.deletedVehicles"
            :items="deletedOptions"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-trash-can"/>
        </v-col>
        <v-col xl="4" lg="4" md="6" cols="12">
          <v-text-field
            :label="$t('ref_id')"
            v-model="searchCriteria.refId"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-barcode">
          </v-text-field>
        </v-col>
      </v-row>
      <v-row v-if="user.can('view_rootentity')">
        <v-col class="d-flex align-center" xl="4" lg="4" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.updatedAfter"
            :timezone="customerTimezone"
            :label="$t('last_updated_after')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
          <date-picker
            v-model="searchCriteria.updatedBefore"
            :timezone="customerTimezone"
            prepend-inner-icon="mdi-calendar"
            :label="$t('last_updated_before')"
            reset-on-timezone-change
            end-of-day />
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
        <vf-tooltip :text="$t('download')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <!-- Data Table -->
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        :item-class="rowClassCallback"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">
        <template #item.name="row">
          <router-link :to="`/vehicle/${row.item.id}`">
            <v-icon small color="primary" class="mr-1">mdi-truck</v-icon>
            {{ row.value }}
            <span v-if="row.item.deleted">({{ $t('deleted') }})</span>
          </router-link>
        </template>
        <template #item.state_updated="row">
          <multi-time-display :time="row.value" />
        </template>
        <template #item.service_provider.name="row">
          <if-set :value="row.value">
            {{row.value}}
          </if-set>
        </template>
        <template #item.ref_id="row">
          <if-set :value="row.value">
            {{row.value}}
          </if-set>
        </template>
        <template #item.gps="row">
          <if-set :value="row.item.hasGpsCoordinates()">
            <vf-tooltip :text="row.item.getGpsCoordinatesAsString()">
              <a :href="`https://maps.google.com/maps?q=${row.item.getGpsCoordinatesAsString()}`">
                <v-icon :color="row.item.hasZeroedGpsCoordinates() ? 'danger' : 'primary'">mdi-map-marker</v-icon>
              </a>
            </vf-tooltip>
          </if-set>
        </template>
        <template #item.gps_updated="row">
          <if-set :value="row.item.getMostRecentGpsReading()">
            <template #default="{ value }">
              <multi-time-display :time="value" />
            </template>
          </if-set>
        </template>
        <template #item.freezer="row">
          <if-set :value="row.item.currentZoneTemp('freezer')">
            <template #default="{ value }">
              {{temperatureToCustomerScale(value)}} &deg;{{ customerContext.tempFormat() }}
            </template>
          </if-set>
        </template>
        <template #item.freezer_updated="row">
          <if-set :value="row.item.getMostRecentZoneReading('freezer')">
            <template #default="{ value } ">
              <multi-time-display :time="value" />
            </template>
          </if-set>
        </template>
        <template #item.cooler="row">
          <if-set :value="row.item.currentZoneTemp('cooler')">
            <template #default="{ value }">
              {{temperatureToCustomerScale(value)}} &deg;{{ customerContext.tempFormat() }}
            </template>
          </if-set>
        </template>
        <template #item.cooler_updated="row">
          <if-set :value="row.item.getMostRecentZoneReading('cooler')">
            <template #default="{ value }">
              <multi-time-display :time="value" />
            </template>
          </if-set>
        </template>
        <template #item.in_route="row">
          <if-set :value="row.item.route" dash>
            <template #default="{ value }">
              <router-link :to="`/route/${value.id}`">
                <span class="text-truncate">
                  <v-icon color="primary">mdi-road-variant</v-icon>
                  {{ value.name }}
                </span>
              </router-link>
            </template>
          </if-set>
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import VfTooltip from '@/components/shared/VfTooltip'
import IfSet from '@/components/shared/IfSet'
import DatePicker from '@/components/shared/DatePicker'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import QueryParamMixin from '@/components/mixins/QueryParamMixin'
import { isEqual, some } from 'lodash'

export default {
  name: 'VehicleIndex',
  components: {
    MultiTimeDisplay,
    TablePaginationControls,
    VfTooltip,
    IfSet,
    DatePicker
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    TemperatureMixin,
    BrowserUtilitiesMixin,
    QueryParamMixin
  ],
  data () {
    return {
      model: 'Vehicle',
      searchCriteria: this.getDefaultSearchCriteria(),
      deletedOptions: [
        {
          value: 'exclude',
          text: this.$t('exclude_deleted')
        },
        {
          value: 'include',
          text: this.$t('include_deleted')
        },
        {
          value: 'show_only',
          text: this.$t('only_show_deleted')
        }
      ],
      queryParams: [
        {
          name: 'name',
          type: String
        },
        {
          name: 'refId',
          type: String
        },
        {
          name: 'deletedVehicles',
          type: String,
          validator: value => some(this.deletedOptions, x => x.value === value)
        }
      ]
    }
  },
  computed: {
    /**
     * Returns a flag that indicates if the search criteria and table navigation (paging/sorting) match the default
     * values.
     * @return Boolean
     */
    tableControlsMatchDefaults () {
      return isEqual(this.searchCriteria, this.getDefaultSearchCriteria()) &&
        this.remoteData.currentPage === 1 &&
        this.remoteData.sortBy === null &&
        this.remoteData.sortDesc === false
    },
    tableFields () {
      const fields = [
        {
          value: 'name',
          text: this.$t('name'),
          sortable: true
        },
        {
          value: 'service_provider.name',
          text: this.$t('service_provider'),
          sortable: false
        },
        {
          value: 'ref_id',
          text: this.$t('ref_id'),
          sortable: true
        },
        {
          value: 'gps',
          text: 'GPS',
          align: 'end',
          sortable: false
        },
        {
          value: 'gps_updated',
          text: this.$t('updated'),
          sortable: false
        },
        {
          value: 'freezer',
          text: this.$t('freezer_air'),
          align: 'end',
          sortable: false
        },
        {
          value: 'freezer_updated',
          text: this.$t('updated'),
          sortable: false
        },
        {
          value: 'cooler',
          text: this.$t('cooler_air'),
          align: 'end',
          sortable: false
        },
        {
          value: 'cooler_updated',
          text: this.$t('updated'),
          sortable: false
        },
        {
          value: 'in_route',
          text: this.$tc('current_route'),
          sortable: false
        }
      ]
      if (this.userLoaded && this.user.can('view_rootentity')) {
        fields.splice(1, 0, {
          value: 'primary_customer.name',
          text: this.$t('primary_customer'),
          class: 'root-only',
          cellClass: 'root-only',
          sortable: false
        })
        fields.splice(3, 0, {
          value: 'state_updated',
          text: this.$t('last_update'),
          class: 'root-only',
          cellClass: 'root-only',
          sortable: true
        })
      }
      return fields
    }
  },
  methods: {
    /**
     * Sets our data table back to its original state before the user applied filtering and paging changes.
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.resetQueryParams()
      this.initiateSearch()
    },
    /**
     * Returns the default search criteria for this data table.
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        name: null,
        refId: null,
        deletedVehicles: 'exclude',
        updatedBefore: null,
        updatedAfter: null
      }
    },
    /**
     * Constructs an object of key value pairs that represent the current search criteria.
     * @return object
     */
    buildSearchCriteria () {
      const expand = [
        'service_provider',
        'route',
        'primary_customer'
      ]
      const fields = [
        'id',
        'name',
        'deleted',
        'service_provider.name',
        'ref_id',
        'state',
        'route.id',
        'route.name',
        'primary_customer.name',
        'state_updated'
      ]
      const searchCriteria = {
        customer_id: this.customerContext.id,
        expand: expand.join(','),
        fields: fields.join(','),
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage,
        deleted_scope: this.searchCriteria.deletedVehicles
      }
      if (this.searchCriteria.name !== null) {
        searchCriteria.name = this.searchCriteria.name
      }
      if (this.searchCriteria.refId !== null) {
        searchCriteria.ref_id = this.searchCriteria.refId
      }
      if (this.searchCriteria.updatedAfter !== null) {
        searchCriteria.updated_after = this.searchCriteria.updatedAfter
      }
      if (this.searchCriteria.updatedBefore !== null) {
        searchCriteria.updated_before = this.searchCriteria.updatedBefore
      }
      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }
      if (this.$route.query.vehicle_id) {
        searchCriteria.vehicle_id = this.$route.query.vehicle_id
      }
      return searchCriteria
    },
    rowClassCallback (row) {
      return row.deleted != null ? 'deleted' : ''
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$tc('vehicle', 2))
    this.useQueryParamsForSearch()
    this.initiateSearch()
  }
}
</script>
